import { Outlet, useRouterState, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Sidebar } from '@/components/layout/Sidebar';
import { Header } from '@/components/layout/Header';
import { MarketTicker } from '@/components/layout/MarketTicker';
import { StatusFooter } from '@/components/layout/StatusFooter';
// NarrativeBar moved to per-route mounts (Builder + Solver + Public Deal Link
// only) by `agent-ux-optimization` Phase 6 Session 7 (2026-05-08). Cockpit /
// Pipeline / Settings / Admin no longer render it.
import { useAuth } from '@/hooks/use-auth';
import { useAdminStatus } from '@/hooks/use-admin-status';
import { useApprovalStatus } from '@/hooks/use-approval-status';
import { useUserProfile } from '@/hooks/use-user-profile';
import { useShareRealtime } from '@/hooks/use-share-realtime';
import { useBillingUsage } from '@/hooks/use-billing-usage';
import {
  WelcomeTour,
  hasSeenWelcomeTour,
  migrateLegacyWelcomeTourSeenKey,
} from '@/components/onboarding';
import { MonthlyAllowanceBanner } from '@/components/billing';
import { FocusResumeCoordinator } from '@/components/FocusResumeCoordinator';
import { PendingApprovalPage } from './pending-approval';
import {
  computeRedirectTarget,
  isAuthRoute as isAuthRouteHelper,
  isPublicRoute as isPublicRouteHelper,
} from './__root.helpers';

export function RootLayout() {
  const { user, loading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdminStatus(user?.id);
  const { isApproved, isLoading: approvalLoading } = useApprovalStatus();
  const {
    profile,
    needsOnboarding,
    isLoading: profileLoading,
  } = useUserProfile();
  const { data: balance, isLoading: balanceLoading } = useBillingUsage();
  // Mount the per-user shares Realtime subscriber once at the root. The hook
  // gates internally on `useAuth().user?.id` being non-null, so this is safe
  // to call unconditionally per the Rules of Hooks. Side-effect only — return
  // value (`{status}`) is unused at the root; consumers read the invalidated
  // queries directly via `useUnreadShareCount` etc. (external-sharing Phase 3
  // Session 3.1, 2026-05-09.)
  useShareRealtime();
  // WelcomeTour modal visibility — controlled by the auto-trigger effect
  // below (first /cockpit entry while approved + !seen) and the
  // AccountSettings "Replay tour" path (which calls resetWelcomeTour() then
  // navigates to /cockpit, re-arming the effect). Declared above the
  // early-returns to satisfy Rules of Hooks. signup-billing-mvp Phase 3
  // Session 3 (2026-05-13).
  const [showTour, setShowTour] = useState(false);
  const routerState = useRouterState();
  const navigate = useNavigate();
  const pathname = routerState.location.pathname;

  const isAuthRoute = isAuthRouteHelper(pathname);
  const isPublic = isPublicRouteHelper(pathname);

  // Consolidated redirect cascade — signup-billing-mvp Phase 5 Session 7
  // (2026-05-13). Pre-Session 7, four separate `useEffect` blocks handled
  // !user / auth-bounce / cockpit-bounce / onboarding-backfill respectively;
  // the 5th + 6th branches (!balance + balance.locked) had no implementation.
  // Open Decision 2 of the Phase 5 design consolidated everything into a
  // single useEffect + pure `computeRedirectTarget(state)` helper so the
  // ladder is unit-testable in isolation (see __root.helpers.test.ts matrix
  // tests) and admin-bypass short-circuits BEFORE the billing check.
  useEffect(() => {
    // Skip while any prereq is still loading — prevents flashy mid-load
    // bounces. `balance` query is `enabled: !!user` so balanceLoading is
    // truthy until either user resolves OR the response lands.
    if (loading) return;
    if (user && (adminLoading || profileLoading || balanceLoading)) return;

    // Parse ?conversation= from the current URL search string so the cascade
    // can thread it through to /billing/checkout when the user lacks a
    // subscription. (in-app-checkout Phase 3 Session 3.7.)
    const searchStr = routerState.location.searchStr ?? '';
    const convMatch = searchStr.match(/[?&]conversation=([^&]+)/);
    const conversationParam = convMatch
      ? decodeURIComponent(convMatch[1])
      : undefined;
    const target = computeRedirectTarget({
      user: user ?? null,
      isAdmin,
      profile: profile ?? null,
      balance: balance ?? null,
      currentPath: pathname,
      conversation: conversationParam,
    });
    if (target && target !== pathname) {
      // Split any `?conversation=` search param out of the target string so
      // TanStack Router's navigate receives the route + search separately.
      // The cascade returns typed route strings; the `as any` cast is
      // necessary because the target is a string union not a literal type and
      // TanStack Router's navigate overloads don't accept a generic string.
      const [targetPath, targetSearch] = target.split('?');
      if (targetSearch) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const navOptions: any = {
          to: targetPath,
          search: Object.fromEntries(new URLSearchParams(targetSearch)),
        };
         
        navigate(navOptions);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        navigate({ to: targetPath as any });
      }
    }
  }, [
    loading,
    user,
    isAdmin,
    profile,
    balance,
    pathname,
    adminLoading,
    profileLoading,
    balanceLoading,
    navigate,
  ]);

  // WelcomeTour auto-trigger — fires on first /cockpit entry for any approved
  // user (admin or not). Gate on `!approvalLoading` (DB-backed via
  // useApprovalStatus per auth-lifecycle-hardening) to avoid flashing the
  // modal during the first DB approval resolution. The AccountSettings
  // "Replay tour" handler clears the localStorage flag then navigates to
  // /cockpit, which re-fires this effect. Strict-mode safe: the second
  // mount sees hasSeenWelcomeTour() === true (if Skip/Finish ran) or stays
  // true (typical mid-tour state), so the duplicate setShowTour(true) is a
  // batched no-op. signup-billing-mvp Phase 3 Session 3 (2026-05-13).
  useEffect(() => {
    if (!user?.id) return;
    // 2026-05-15: one-shot migrate legacy global `arcanum_welcome_tour_seen`
    // → per-user `arcanum_welcome_tour_seen:<userId>`. Safe to call every
    // render; no-op once migrated. Attributes the legacy flag to the
    // currently-active user — same-browser pre-fix code only had one
    // logged-in user at a time, so this is the correct interpretation.
    migrateLegacyWelcomeTourSeenKey(user.id);
    if (
      pathname === '/cockpit' &&
      isApproved &&
      !approvalLoading &&
      !hasSeenWelcomeTour(user.id)
    ) {
      setShowTour(true);
    }
  }, [pathname, isApproved, approvalLoading, user?.id]);

  // Initial Supabase session probe still in flight — show full-screen loader.
  // We deliberately don't gate on `approvalLoading` here because the approval
  // check only runs once `user` resolves; folding it in would lock auth-routes
  // behind a query that won't fire for unauthenticated visitors.
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-void gap-3">
        <div className="flex items-center gap-3">
          <svg width="20" height="24" viewBox="0 0 16 18">
            <rect
              x="0"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
            <rect
              x="8"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="text-sm font-mono text-text-dim">Loading...</span>
        </div>
      </div>
    );
  }

  // Auth pages render without the shell
  if (isAuthRoute) {
    return <Outlet />;
  }

  // Public routes (/deal/*) render their own chrome — bypass the
  // approval/onboarding gate and the authenticated shell entirely.
  // The deal page itself handles authenticated-viewer redirects (I3).
  if (isPublic) {
    return <Outlet />;
  }

  // `/billing/checkout` is a gated full-screen tier-purchase surface — render
  // bare (no Sidebar/Header/MarketTicker). Two reasons: (a) navigation links
  // would be misleading because the cascade redirects non-admin/non-paying
  // users back to /billing/checkout on every click; (b) the shell's
  // `<main className="overflow-hidden">` clips the tall tier cards. The
  // checkout page owns its own ARCANUM branding + scrolling. signup-billing
  // -mvp Phase 6 carryover UX fix 2026-05-14.
  if (pathname === '/billing/checkout') {
    return <Outlet />;
  }

  // Not authenticated — show nothing while redirect happens
  if (!user) {
    return null;
  }

  // Approval status query OR profile query still in flight — render the
  // loader rather than briefly flashing the under-review page on the happy
  // path. Includes profile query because the existing-user-backfill redirect
  // (signed in + needsOnboarding → /signup) is gated on profileLoading; if
  // we render PendingApprovalPage during that window we get a brief flash.
  if (approvalLoading || profileLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-void gap-3">
        <div className="flex items-center gap-3">
          <svg width="20" height="24" viewBox="0 0 16 18">
            <rect
              x="0"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
            <rect
              x="8"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="text-sm font-mono text-text-dim">Loading...</span>
        </div>
      </div>
    );
  }

  // /gap-analysis fix (signup-and-billing Phase 1, 2026-05-04): authenticated
  // user with onboarding incomplete is being redirected to /signup by the
  // useEffect above. Render the loader (NOT PendingApprovalPage) during the
  // single-tick window between profileLoading=false and the navigate side
  // effect taking effect. Without this branch, an approved-but-not-onboarded
  // user (backfill scenario) would briefly see the cockpit OR the pending
  // page before being routed to the wizard.
  if (needsOnboarding) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-void gap-3">
        <div className="flex items-center gap-3">
          <svg width="20" height="24" viewBox="0 0 16 18">
            <rect
              x="0"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
            <rect
              x="8"
              y="0"
              width="5"
              height="18"
              rx="3"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="text-sm font-mono text-text-dim">Loading...</span>
        </div>
      </div>
    );
  }

  // Authenticated but not approved — show pending page
  if (!isApproved) {
    return <PendingApprovalPage />;
  }

  // `deal-signal-overhaul` Phase 5 redesign (2026-05-12) — suppress the
  // global market ticker on Builder's Signal + Model tabs. The Signal page
  // is its own data-dense surface (hero KPIs, value-add narrative, capital
  // stack) and the Model tab is a 3-pane spreadsheet view — both compete
  // with the ticker for vertical real-estate and for cognitive bandwidth.
  // Workbench keeps the ticker (chat workflow tolerates the chrome).
  const showTicker = !(
    pathname === '/builder' &&
    /[?&]tab=(signal|model)\b/.test(routerState.location.searchStr ?? '')
  );

  return (
    <div className="flex h-screen items-stretch bg-void font-body text-text overflow-hidden">
      <FocusResumeCoordinator />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Header pathname={pathname} />
        <MonthlyAllowanceBanner />
        {showTicker && <MarketTicker />}
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
        <StatusFooter />
      </div>
      {showTour && <WelcomeTour onClose={() => setShowTour(false)} />}
    </div>
  );
}
